import { cloneDeep } from "lodash";
import moment from "moment";
import i18nService from "@/services/i18n";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { reportReconStatementFilterOptions } from "../report-recon-statement";
import { ReportReconStatementFilterSettingsForm } from "../settings-form/ReportReconStatementFilterSettingsForm";
import { ReportReconStatementFilterSettings } from "../settings/ReportReconStatementFilterSettings";
import { ReportReconStatementRow } from "../table-data/ReportReconStatementRow";
import { ReportReconStatementJournalRequest } from "../journal/ReportReconStatementJournalRequest";
import { ReportReconStatementTableDataRequest } from "../data-request/ReportReconStatementTableDataRequest";
import { Segmentation } from "../../../common/types/roots/fields";
import {Draggable} from "@/modules/budget/monitoring/reports-constructor/common/types/Draggable";

export class ReportReconStatementFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(reportReconStatementFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'RECON-STATEMENT') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'RECON-STATEMENT') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        let measureUnitOption = options.measureUnits.find(it => it.value === settings.measureUnit)
        if (!measureUnitOption) {
            throw new Error('error_not_found_measure_unit')
        }
        measureUnitOption = { ...measureUnitOption }

        let mutuallyRedeemingOption = options.mutuallyRedeeming.find(it => it.value === settings.mutuallyRedeeming)
        if (!mutuallyRedeemingOption) {
            throw new Error('error_not_found_mutually_redeeming_option')
        }
        mutuallyRedeemingOption = { ...mutuallyRedeemingOption }

        let segmentationOptions = options.segmentations
        if (!segmentationOptions) {
            throw new Error('error_not_found_segmentation_option')
        }
        segmentationOptions = [ ...segmentationOptions ]
        const budgetTypeOptions = cloneDeep(
            options.budgetTypes.filter(it => settings.budgetTypes.includes(it.value))
        )

        const statusOptions = cloneDeep(
            options.statuses.filter(it => settings.statuses.includes(it.value))
        )

        const mutuallyRedeemingLevelOptions = cloneDeep(
            options.mutuallyRedeemingLevels.filter(it => settings.mutuallyRedeemingLevels.includes(it.value))
        )

        let regionOption = options.regions.find(it => it.value === settings.region)
        if (!regionOption) {
            throw new Error('error_not_found_region_option')
        }
        regionOption = { ...regionOption }


        let isRoundUp: boolean = false
        if (settings.roundUpTo != null) {
            isRoundUp = true
        }
        const allSegmentationOptions: Draggable<Segmentation>[] = [
            { value: 'REGION', label: "Регион", active: false },
            { value: 'KBK', label: "КБК", active: false }
        ]

        const month = options.months.find(it => it.value === settings.month)
        if (!month) {
            throw new Error('error_unknown_month')
        }


        // 2. Формирование
        const form: ReportReconStatementFilterSettingsForm = {
            // Base:
            key: 'RECON-STATEMENT',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: new Date(settings.date),
            month: month,
            year: settings.year,
            measureUnit: measureUnitOption,
            budgetTypes: budgetTypeOptions,
            statuses: statusOptions,
            isTree: settings.isTree,
            mutuallyRedeeming: mutuallyRedeemingOption,
            mutuallyRedeemingLevels: mutuallyRedeemingLevelOptions,
            segmentations: allSegmentationOptions,
            region: regionOption,
            roundUp: isRoundUp,
            roundUpTo: settings.roundUpTo,
            incomeTotal: settings.incomeTotal,
            incomes: cloneDeep(settings.incomes),
            expenseTotal: settings.expenseTotal,
            expenses: cloneDeep(settings.expenses),
            indicators: cloneDeep(settings.indicators),
            fillCbc: settings.fillCbc,
            show_deviation_only: settings.show_deviation_only,
            compared_period: settings.compared_period,
            gu: settings.gu
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'RECON-STATEMENT') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }

        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }

        if (!form.region) {
            throw new Error('error_input_region')
        }

        if (!form.segmentations) {
            throw new Error('error_input_segmentation')
        }


        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: ReportReconStatementFilterSettings = {
            // Base:
            reportName: 'RECON-STATEMENT',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            month: 1,
            year: 2025,
            budgetTypes: form.budgetTypes.map(it => it.value),
            region: form.region.value,
            statuses: form.statuses.map(it => it.value),
            isTree: form.isTree,
            mutuallyRedeeming: form.mutuallyRedeeming.value,
            mutuallyRedeemingLevels: form.mutuallyRedeemingLevels.map(it => it.value),
            segmentations: form.segmentations,
            measureUnit: form.measureUnit.value,
            roundUpTo: roundUpTo,
            incomeTotal: form.incomeTotal,
            incomes: cloneDeep(form.incomes),
            expenseTotal: form.expenseTotal,
            expenses: cloneDeep(form.expenses),
            indicators: cloneDeep(form.indicators),
            fillCbc: form.fillCbc ?? false,
            show_deviation_only: form.show_deviation_only,
            compared_period: form.compared_period,
            gu: form.gu
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(reportReconStatementFilterOptions)

        const yesterdayMoment = moment().subtract(1, 'days')
        const yesterdayDate = yesterdayMoment.toDate()
        const today =  new Date()
        const month = options.months[today.getMonth()]
        let compPeriodOption = options.comparedPeriods[0]
        const find = options.comparedPeriods.find(it => it.value === 'MONTH')
        if (find) {
            compPeriodOption = find
        }
        const emptyForm: ReportReconStatementFilterSettingsForm = {
            key: 'RECON-STATEMENT',
            periodicity: options.periodicities.find(it => it.value === 'MONTH')!,
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'RECON-STATEMENT')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'INCOMES')!,
            dataSource: options.dataSources.find(it => it.value !== 'MIXED')!,
            // Custom fields
            date: yesterdayDate,
            year: today.getFullYear(),
            month: month,
            measureUnit: options.measureUnits.find(it => it.value === 'THOUSAND')!,
            budgetTypes: [],
            region: null,
            mutuallyRedeeming: null,
            mutuallyRedeemingLevels: options.mutuallyRedeemingLevels,
            statuses: options.statuses.filter(it => it.value === '39'),
            isTree: true,
            segmentations: [],
            roundUp: false,
            roundUpTo: null,
            incomeTotal: false,
            incomes: cloneDeep(options.incomes),
            expenseTotal: false,
            expenses: cloneDeep(options.expenses),
            indicators: cloneDeep(options.indicators),
            fillCbc: false,
            show_deviation_only: false,
            compared_period: compPeriodOption,
            gu: null
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        const classificationType = form.classificationType.value
        if (form.key !== 'RECON-STATEMENT') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (classificationType === 'EXPENSES' && !form.date) {
            throw new Error('error_input_date')
        }
        if (classificationType === 'INCOMES' && (!form.year || !form.month)) {
            throw new Error('error_input_year_month')
        }
        if (!form.measureUnit) {
            throw new Error('error_input_measure_unit')
        }
        if (!form.region) {
            throw new Error('error_input_region')
        }
        if (!form.mutuallyRedeeming) {
            throw new Error('error_input_mutually_redeeming')
        }
        if (!form.segmentations?.filter(it => it.active).length) {
            throw new Error('error_input_segmentation')
        }

        const cutness: string[] | undefined = form.segmentations?.filter(v => v.active).map(v => v.value)
        if (cutness == null) {
            throw new Error('error_unsupported_segmentation_type')
        }

        let roundUpTo: number | null = null
        if (form.roundUp) {
            roundUpTo = form.roundUpTo ?? null
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        let classifications: string[] = []
        let activeIncomes = form.incomes
            .filter(item => item.active)
        let activeExpenses = form.expenses
            .filter(item => item.active)
        let incomeTotal = form.incomeTotal
        let expenseTotal = form.expenseTotal

        if (classificationType === 'INCOMES') {
            activeExpenses = []
            expenseTotal = false
            classifications = activeIncomes.map(it => it.key)
        } else if (classificationType === 'EXPENSES') {
            activeIncomes = []
            incomeTotal = false
            classifications = activeExpenses.map(it => it.key)
        } else {
            throw new Error('error_unsupported_class_type')
        }

        const vzkList = form.mutuallyRedeemingLevels.map(it => {
            if (it.value === 'TYPE-3') {
                return 3
            } else if (it.value === 'TYPE-4') {
                return 4
            } else {
                throw new Error('error_unsupported_vzk_level')
            }
        })

        let source: 'integration' | 'loader' = 'integration'
        if (form.dataSource.value === 'IISK') {
            source = 'integration'
        } else if (form.dataSource.value === 'LOADER') {
            source = 'loader'
        } else {
            throw new Error('error_unsupported_data_source')
        }

        if (!form.segmentations?.map(v => v.value).includes('KBK')) {
            activeIncomes = []
            activeExpenses = []
        }

        let filterGU:string|null = null
        if (form.gu) {
            filterGU = form.gu.value
        }

        const params: ReportReconStatementTableDataRequest = {
            user_id: userId,
            incomes: form.incomes,
            expenses: form.expenses,
            repdate: strDate,
            month: form.month?.value,
            year: form.year,
            region: form.region.value,
            source: source,
            cutness: cutness,
            tree_view: form.isTree,
            mutually_redeeming: form.mutuallyRedeeming.value,
            vzk_list: vzkList,
            budget_types: form.budgetTypes.map(it => it.value),
            status_list: form.statuses.map(it => it.value),
            income_total: incomeTotal,
            expense_total: expenseTotal,
            measure_unit: form.measureUnit.value,
            round_up_to: roundUpTo,
            locale: i18nService.locale,
            fill_cbc: form.fillCbc,
            show_deviation_only: form.show_deviation_only,
            compared_period: form.compared_period.value,
            gu: filterGU
        }

        let endpoint = ''
        const indicatorsOrder: string[] = form.indicators.filter(it => it.active).map(it => it.key)
        switch (classificationType) {
            case 'INCOMES': {
                endpoint = '1-27'
                break
            }
            case 'EXPENSES': {
                endpoint = '4-20';
                break;
            }
            default: throw new Error('error_type_classification')
        }

        const response = await fetch(`/api-py/monitoring/reports-constructor/recon-statement/${endpoint}`,
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: any[] = await response.json()
        const firstClsn = classifications[0]
        const lastClsn = classifications[classifications.length - 1]
        const idMap:any = {}
        for (let i = 0; i < form.segmentations?.length; i++) {
            idMap[form.segmentations[i].value] = i
        }
        const tableData: ReportReconStatementRow[] = responseTableData.map((row: any, index) => {
            row.index = index


            // if (form.segmentations) {
            //     if (row.region) {
            //         if (idMap['REGION'] > idMap['KBK']) {
            //             row[lastClsn] = row.region
            //         } else {
            //             row[firstClsn] = row.region
            //         }
            //     }
            //     if (row.gu) {
            //         if (idMap['GU'] > idMap['KBK']) {
            //             row[lastClsn] = row.gu
            //         } else {
            //             row[firstClsn] = row.gu
            //         }
            //     }
            // }


            // if (!form.isTree && row[lastClsn]) {
            //     if (typeof row.name === 'string' && row.name) {
            //         row.name = `${row[lastClsn]} - ${row.name}`
            //     } else {
            //         row.name = row[lastClsn]
            //     }
            // }

            return row
        })

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: ReportReconStatementJournalRequest = {
            reportName: "RECON-STATEMENT",
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            columns_list: classifications,
            indicators_order: indicatorsOrder, // Может быть редактирована во время превью
            cutness: cutness,
            params: params,
            locale: i18nService.locale,
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
